import React, { useEffect } from "react";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import { Box, Grid2, IconButton, Tooltip, useTheme } from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import CustomTypography from "../../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../../../views/components/common/textFields/TextField";
import DatePicker2 from "../../../../../../../views/components/common/textFields/DatePicker2";
import InlineFormError from "../../../../../../../views/components/common/form/InlineFormError";
import AppSearchDropdownMulti from "../../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import {
  getModernPageColors,
  modernSectionPaperSx,
} from "../../../../../../../styles/modernListingPageSx";
import { useStores } from "../../../../../use-stores";
import { useNotification } from "../../../../../../../utils/NotificationsContext";
import SwageazyInventoryAdjustmentStore from "../../../../../stores/SwageazyInventoryAdjustmentStore";

interface AdjustNewSkuLinesProps {
  store: SwageazyInventoryAdjustmentStore;
}

/**
 * Adds stock for any product SKU into a box: Warehouse → Rack → Box ID
 * (typed or generated), same as Inward.
 */
const AdjustNewSkuLines: React.FC<AdjustNewSkuLinesProps> = ({ store }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const { warehouseStore } = useStores();
  const { showError } = useNotification();

  useEffect(() => {
    if (!store.isAdjustOpen || warehouseStore.warehouses.length > 0) return;
    warehouseStore.searchWarehouse().catch(() => {
      showError("Error", "Failed to fetch warehouses.");
    });
  }, [store.isAdjustOpen, warehouseStore, showError]);

  const warehouseOptions = warehouseStore.warehouses.map((warehouse) => ({
    id: String(warehouse.id),
    value: String(warehouse.identifier || warehouse.id),
  }));
  const disabled = store.isSubmitting;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Box>
          <CustomTypography variant="heading7" color={colors.title}>
            Add SKU
          </CustomTypography>
          <CustomTypography variant="caption" color={colors.subtitle}>
            Add stock for any SKU of this product into a box.
          </CustomTypography>
        </Box>
        <AppButton
          variant="transparentPrimary"
          label="Add SKU"
          startIcon={<AddIcon />}
          onClick={store.addNewLine}
          disabled={disabled || store.isProductSkusLoading}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {store.newLines.map((line) => {
          const error = store.rowErrors[`new:${line.id}`];
          const racks = store.racksByWarehouse[line.warehouse_id] || [];
          return (
            <Box
              key={line.id}
              sx={{
                ...modernSectionPaperSx(theme),
                borderColor: error ? theme.palette.error.main : colors.border,
              }}
            >
              <Grid2 container spacing={1.5} sx={{ alignItems: "flex-end" }}>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  <AppSearchDropdownMulti
                    title="SKU"
                    isMultiSelect={false}
                    local
                    floatingLabel
                    list={store.productSkuOptions}
                    value={store.productSkuOptions.filter(
                      (option) => option.id === line.sku,
                    )}
                    handleSelect={(selected) =>
                      store.updateNewLine(line.id, {
                        sku: selected?.[0]?.id || "",
                      })
                    }
                    disabled={disabled}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  <AppSearchDropdownMulti
                    title="Warehouse"
                    isMultiSelect={false}
                    local
                    floatingLabel
                    list={warehouseOptions}
                    value={warehouseOptions.filter(
                      (option) => option.id === line.warehouse_id,
                    )}
                    handleSelect={(selected) =>
                      store.updateNewLine(line.id, {
                        warehouse_id: selected?.[0]?.id || "",
                      })
                    }
                    disabled={disabled}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  <AppSearchDropdownMulti
                    title="Rack (to generate a box)"
                    isMultiSelect={false}
                    local
                    floatingLabel
                    list={racks}
                    value={racks.filter((option) => option.id === line.rack)}
                    handleSelect={(selected) =>
                      store.updateNewLine(line.id, {
                        rack: selected?.[0]?.id || "",
                      })
                    }
                    disabled={disabled || !line.warehouse_id}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <CustomTextField
                      placeholder="Box ID e.g. B00001"
                      width="100%"
                      value={line.box_id}
                      disabled={disabled || !line.warehouse_id}
                      onChange={(e) =>
                        store.updateNewLine(line.id, {
                          box_id: e.target.value.toUpperCase(),
                        })
                      }
                    />
                    <AppButton
                      variant="grayscale"
                      label={line.isGeneratingBox ? "..." : "Generate"}
                      onClick={() => {
                        store.generateBoxForLine(line.id).catch((err) => {
                          console.error(err);
                        });
                      }}
                      disabled={
                        disabled ||
                        line.isGeneratingBox ||
                        !line.warehouse_id ||
                        !line.rack ||
                        Boolean(line.box_id)
                      }
                      customStyles={{ minWidth: 96, flexShrink: 0 }}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                  <CustomTextField
                    type="number"
                    allowNegativeValue={false}
                    allowDecimalValue={false}
                    placeholder="Qty to add"
                    width="100%"
                    value={line.quantity}
                    disabled={disabled}
                    onChange={(e) =>
                      store.updateNewLine(line.id, {
                        quantity: e.target.value,
                      })
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <DatePicker2
                    type="date"
                    label="Expiry (optional)"
                    value={line.expiry_date ? dayjs(line.expiry_date) : null}
                    minDate={dayjs()}
                    onChange={(value) =>
                      store.updateNewLine(line.id, {
                        expiry_date: value
                          ? value.format("YYYY-MM-DD")
                          : undefined,
                      })
                    }
                    borderRadius="8px"
                    zIndex={4000}
                    disabled={disabled}
                  />
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6, md: 3 }}
                  sx={{ display: "flex", gap: 1, alignItems: "center" }}
                >
                  <CustomTextField
                    placeholder="Note (optional)"
                    width="100%"
                    value={line.note}
                    disabled={disabled}
                    onChange={(e) =>
                      store.updateNewLine(line.id, { note: e.target.value })
                    }
                  />
                  <Tooltip title="Remove">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="remove SKU line"
                        onClick={() => store.removeNewLine(line.id)}
                        disabled={disabled}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid2>
              </Grid2>
              <InlineFormError message={error} sx={{ mb: 0 }} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default observer(AdjustNewSkuLines);
